import type { ComponentType, SVGProps } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronRight, LogIn, Swords, Trophy } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  clearInbox,
  enqueuePrompts,
  markAllInboxRead,
  markInboxRead,
  selectInboxItems,
  selectUnreadInboxCount,
} from '@/store/engagement.slice';
import { useHaptics } from '@/hooks/useHaptics';
import { defaultPromptBody, defaultPromptTitle } from '@/lib/engagement';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';

const ICON_BY_KIND: Record<string, ComponentType<SVGProps<SVGSVGElement>>> = {
  register: Trophy,
  checkin: LogIn,
  play: Swords,
};

export function Inbox() {
  const dispatch = useAppDispatch();
  const items = useAppSelector(selectInboxItems);
  const unread = useAppSelector(selectUnreadInboxCount);
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { t } = useTranslation('general');
  const lingua = useLinguaFormato();

  const open = (id: string, target: string) => {
    haptics.impact('light');
    dispatch(markInboxRead(id));
    navigate(target);
  };

  // DEV-only: inject a sample prompt so the rising nudge + inbox can be tested
  // without a real tournament in CHECKIN. Stripped from prod (import.meta.env.DEV
  // is false in the production build).
  const injectTest = () => {
    const now = Date.now();
    dispatch(
      enqueuePrompts([
        {
          id: `t.checkin.demo-${now}`,
          dedupeKey: `t.checkin.demo-${now}`,
          source: 'client',
          kind: 'checkin',
          priority: 'urgent',
          titleKey: 'engagement.checkin_title',
          bodyKey: 'engagement.checkin_body',
          params: { tournament: 'Demo Cup' },
          target: '/tournaments/1',
          createdAt: now,
          recoverable: true,
        },
      ]),
    );
    navigate('/');
  };

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 pb-6 pt-4">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. I comandi della posta restano, a destra. */}
      <header className="flex items-center justify-end gap-2">
        {/* `text-2xl` come News e Amici: il titolo di una scheda principale è
            un livello della gerarchia, non una scelta della singola schermata.
            Qui era `text-lg`, e passando fra le schede la testata cambiava
            statura — un dislivello che si legge come un errore di impaginazione
            proprio perché le tre schermate sono sorelle. */}
        <h1 className="sr-only">
          {t('engagement.inbox_title', { defaultValue: 'Inbox' })}
        </h1>
        {items.length > 0 && (
          <div className="flex items-center gap-3 text-xs font-semibold">
            <button
              type="button"
              onClick={() => dispatch(markAllInboxRead())}
              disabled={unread === 0}
              className="text-brand transition-opacity active:opacity-70 disabled:opacity-40"
            >
              {t('engagement.mark_all_read', { defaultValue: 'Mark all read' })}
            </button>
            <button
              type="button"
              onClick={() => dispatch(clearInbox())}
              className="text-fg-muted transition-opacity active:opacity-70"
            >
              {t('engagement.clear', { defaultValue: 'Clear' })}
            </button>
          </div>
        )}
      </header>

      {items.length === 0 ? (
        <div className="mt-10 flex flex-col items-center gap-3 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-bg-secondary text-fg-muted">
            <Bell aria-hidden className="h-7 w-7" />
          </span>
          <p className="text-sm text-fg-muted">
            {t('engagement.inbox_empty', { defaultValue: 'Nothing here yet' })}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {items.map((item) => {
            const Icon = ICON_BY_KIND[item.kind] ?? Trophy;
            const title = t(item.titleKey, {
              defaultValue: defaultPromptTitle(item.kind),
              ...item.params,
            });
            const body = item.bodyKey
              ? t(item.bodyKey, { defaultValue: defaultPromptBody(item.kind), ...item.params })
              : '';
            const unreadItem = item.readAt == null;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => open(item.id, item.target)}
                  className="flex w-full items-center gap-3 rounded-xl bg-bg-secondary p-3 text-left transition-opacity active:opacity-80"
                >
                  <span
                    className={cn(
                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
                      unreadItem ? 'bg-brand/15 text-brand' : 'bg-bg-tertiary text-fg-muted',
                    )}
                  >
                    <Icon aria-hidden className="h-5 w-5" strokeWidth={2.5} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-semibold text-fg">{title}</span>
                    {body && <span className="truncate text-xs text-fg-muted">{body}</span>}
                    <span className="text-[11px] text-fg-muted">
                      {formatRelative(new Date(item.createdAt).toISOString(), lingua)}
                    </span>
                  </span>
                  {unreadItem && (
                    <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-brand" />
                  )}
                  <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-fg-muted" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {import.meta.env.DEV && (
        <button
          type="button"
          onClick={injectTest}
          className="mt-4 self-start rounded-xl border border-dashed border-border px-3 py-2 text-xs font-semibold text-fg-muted transition-opacity active:opacity-70"
        >
          {t('inbox.devInject')}
        </button>
      )}
    </div>
  );
}
